/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import { find } from 'lodash';
import * as React from 'react';

import { MeContext } from 'blb-contexts/Me';
import { cardWidth } from 'blb-styles/constants';
import { BlendBridge } from 'blb-types/blendBridge';
import { formatAddress, titleCase } from 'blb-utils/format';
import { AlertBar, BrandColors, Header, Tag, Text, Tooltip } from '@blend/enterprise-components';
import { getPersona } from 'blb-persona/index';
import { getSupportEmailAnchor, getSupportContactPhoneInfo } from 'blb-utils/contact';

export const styles = StyleSheet.create({
  branch: {
    background: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem',
    padding: `12px 24px`,
    width: cardWidth,
  },
  branchAddress: {
    color: BrandColors.NIGHT_90,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
  },
  branchName: {
    color: BrandColors.NIGHT,
    fontSize: '1rem',
    lineHeight: '1.5rem',
  },
  emailAddress: {
    color: BrandColors.NIGHT_70,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
  },
  headerContainer: {
    fontSize: '1.5em',
    marginBottom: '16px',
    marginTop: '32px',
  },
  memberName: {
    paddingRight: '1rem',
  },
  members: {
    color: BrandColors.NIGHT,
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    marginTop: '0.5rem',
  },
  paragraph: {
    margin: '0.5rem 0',
  },
  role: {
    display: 'block',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  sectionContentContainer: {
    marginBottom: '48px',
  },
});

export const GroupsInfo: React.FC<{
  userGroups: BlendBridge.API.SearchGroupRecord[];
}> = ({ userGroups }) => {
  const me = React.useContext(MeContext)!;
  const persona = getPersona(me.principalType);
  const support = persona.getSupportContactInfo();

  const displayContactInfo = () => {
    return (
      <p>
        <Text>
          If you're interested in trying out branches or need to update your existing ones, please contact{' '}
          {getSupportEmailAnchor(support.email)}
          {getSupportContactPhoneInfo(support)}.
        </Text>
      </p>
    );
  };

  const aboutBranches = (
    <div>
      <Header size={4}>What are branches?</Header>
      <p className={css(styles.paragraph)}>
        <Text>
          Branches are a way to share closings among multiple users. You can think of them as a shared email inbox for
          closings. For instance, you can have all closings sent to <em>branchname@agency.com</em> visible to and
          actionable by all members of the corresponding branch.
        </Text>
      </p>
      {displayContactInfo()}
    </div>
  );

  const zeroState = (
    <p>
      <Text>You're not part of any branches.</Text>
    </p>
  );
  const currentBranches = (
    <ul>
      {userGroups.map(userGroup => {
        const currentUser = find(userGroup.assignments, { principalId: me.principalId })!;
        const owner = find(userGroup.assignments, { role: 'OWNER' })!;
        const memberCount = userGroup.assignments.length - 1; // exclude owner
        const tooltip =
          currentUser === owner
            ? `You are the branch owner. Closings sent to you (${owner.username}) will be visible to and actionable by all members of this branch.`
            : `You are a member of this branch. Closings sent to the branch (${owner.username}) will be visible to and actionable by you and all other members of this branch.`;
        const renderTooltip = () => (
          <Text colorName="WHITE" fontSize="sm">
            {tooltip}
          </Text>
        );
        return (
          <li className={css(styles.branch)} key={userGroup.id}>
            <div className={css(styles.row)}>
              <div className={css(styles.branchName)}>{userGroup.name}</div>
              <Tooltip render={renderTooltip}>
                <Tag className={css(styles.role)} text={titleCase(currentUser.role)} />
              </Tooltip>
            </div>
            <div className={css(styles.branchAddress)}>{formatAddress(userGroup.address)}</div>
            <div className={css(styles.emailAddress)}>{owner.username}</div>
            <details className={css(styles.members)}>
              <summary>
                {memberCount} member{memberCount === 1 ? '' : 's'}
              </summary>
              <ul>
                {userGroup.assignments
                  .filter(assignment => assignment !== owner)
                  .map(assignment => (
                    <li key={`${userGroup.id}_${assignment.principalId}`}>
                      <span className={css(styles.memberName)}>{assignment.name}</span>
                      <span className={css(styles.emailAddress)}>{assignment.username}</span>
                    </li>
                  ))}
              </ul>
            </details>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <AlertBar message={aboutBranches} theme="info" />
      <section className={css(styles.sectionContentContainer)}>
        <Header className={css(styles.headerContainer)} size={2}>
          Current Branches
        </Header>
        {userGroups.length > 0 ? currentBranches : zeroState}
      </section>
    </>
  );
};
